import React, { useRef, useEffect, useMemo, Suspense } from 'react';
import { Canvas, useLoader, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';

// Component to display STL mesh with rotation
function RotatedSTLMesh({ url, rotation, color = '#3b82f6' }) {
  const geometry = useLoader(STLLoader, url);
  const meshRef = useRef();
  const groupRef = useRef();
  const { camera } = useThree();
  
  // Clone and process geometry
  const { processedGeometry, centerOffset, cameraDistance } = useMemo(() => {
    const clone = geometry.clone();
    clone.computeVertexNormals();
    
    // Convert from STL Z-up to Three.js Y-up
    clone.rotateX(-Math.PI / 2);
    
    // Calculate bounding box for centering
    clone.computeBoundingBox();
    const box = clone.boundingBox;
    const center = new THREE.Vector3();
    box.getCenter(center);
    
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    
    return {
      processedGeometry: clone,
      centerOffset: [-center.x, -center.y, -center.z],
      cameraDistance: maxDim * 1.8
    };
  }, [geometry]);
  
  // Set camera position
  useEffect(() => {
    camera.position.set(cameraDistance * 0.7, cameraDistance * 0.5, cameraDistance * 0.7);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }, [camera, cameraDistance]);
  
  // Apply user rotation (convert degrees to radians)
  // Note: rotation is applied in STL coordinate system (before Y-up conversion)
  const rotationRadians = useMemo(() => ({
    x: (rotation.x * Math.PI) / 180,
    y: (rotation.y * Math.PI) / 180,
    z: (rotation.z * Math.PI) / 180,
  }), [rotation]);

  return (
    <group ref={groupRef} position={centerOffset}>
      {/* Apply rotation around center */}
      <group rotation={[rotationRadians.x, rotationRadians.z, -rotationRadians.y]}>
        <mesh ref={meshRef} geometry={processedGeometry}>
          <meshStandardMaterial 
            color={color}
            side={THREE.DoubleSide}
            flatShading={false}
          />
        </mesh>
      </group>
    </group>
  );
}

// Build plate grid
function BuildPlate() {
  return (
    <Grid
      args={[200, 200]}
      cellSize={10}
      cellThickness={0.5}
      cellColor="#444444"
      sectionSize={50}
      sectionThickness={1}
      sectionColor="#666666"
      fadeDistance={400}
      fadeStrength={1}
      position={[0, -0.01, 0]}
      rotation={[0, 0, 0]}
    />
  );
}

// Axis indicator
function AxisIndicator() {
  return (
    <group position={[-80, 0, -80]}>
      {/* X axis - Red */}
      <mesh position={[10, 0, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 20, 8]} />
        <meshBasicMaterial color="#ff0000" />
      </mesh>
      {/* Y axis - Green (up in Three.js) */}
      <mesh position={[0, 10, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.5, 0.5, 20, 8]} />
        <meshBasicMaterial color="#00ff00" />
      </mesh>
      {/* Z axis - Blue */}
      <mesh position={[0, 0, 10]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 20, 8]} />
        <meshBasicMaterial color="#0000ff" />
      </mesh>
    </group>
  );
}

// Loading fallback
function LoadingFallback() {
  return (
    <mesh>
      <boxGeometry args={[10, 10, 10]} />
      <meshBasicMaterial color="#444444" wireframe />
    </mesh>
  );
}

// Main preview component
export function OrientationPreview({ fileUrl, rotation, height = 300 }) {
  if (!fileUrl) {
    return (
      <div 
        className="bg-bg-dark rounded-lg flex items-center justify-center border border-text-muted/30"
        style={{ height }}
      >
        <p className="text-text-muted">Upload a model to preview orientation</p>
      </div>
    );
  }

  return (
    <div 
      className="relative bg-bg-dark rounded-lg overflow-hidden border border-text-muted/30"
      style={{ height }}
    >
      <Canvas
        camera={{ 
          position: [100, 80, 100], 
          fov: 45,
          near: 0.1,
          far: 10000
        }}
        gl={{ antialias: true }}
      >
        <color attach="background" args={['#1a1a2e']} />
        
        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight position={[100, 100, 50]} intensity={0.8} />
        <directionalLight position={[-50, 50, -50]} intensity={0.3} />
        <directionalLight position={[0, -50, 0]} intensity={0.2} />
        
        {/* Build plate and axis */}
        <BuildPlate />
        <AxisIndicator />
        
        {/* Model with rotation */}
        <Suspense fallback={<LoadingFallback />}>
          <RotatedSTLMesh 
            url={fileUrl} 
            rotation={rotation}
            color="#3b82f6"
          />
        </Suspense>
        
        {/* Camera controls */}
        <OrbitControls 
          enableDamping 
          dampingFactor={0.05}
          minDistance={20}
          maxDistance={500}
        />
      </Canvas>
      
      {/* Rotation info overlay */}
      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
        X: {rotation.x}° | Y: {rotation.y}° | Z: {rotation.z}°
      </div>
    </div>
  );
}

export default OrientationPreview;
